import {
    collection,
    doc,
    getDoc,
    getDocs,
    addDoc,
    setDoc,
    updateDoc,
    deleteDoc,
    query,
    where,
    orderBy,
    limit,
    startAfter,
    onSnapshot,
    DocumentData,
    QueryConstraint,
    DocumentReference,
    CollectionReference,
    Timestamp,
    GeoPoint,
    WhereFilterOp,
    OrderByDirection,
} from 'firebase/firestore';
import { db } from './config';

/**
 * Get a document by ID
 */
export const getDocument = async <T = DocumentData>(
    collectionName: string,
    documentId: string
): Promise<T | null> => {
    const docRef = doc(db, collectionName, documentId);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
        return { id: docSnap.id, ...docSnap.data() } as T;
    }
    return null;
};

/**
 * Get multiple documents with query
 */
export const getDocuments = async <T = DocumentData>(
    collectionName: string,
    constraints: QueryConstraint[] = []
): Promise<T[]> => {
    const collectionRef = collection(db, collectionName);
    const q = query(collectionRef, ...constraints);
    const querySnapshot = await getDocs(q);

    return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
    })) as T[];
};

/**
 * Add a new document
 */
export const addDocument = async <T = DocumentData>(
    collectionName: string,
    data: T
): Promise<string> => {
    const collectionRef = collection(db, collectionName);
    const docRef = await addDoc(collectionRef, {
        ...data,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
    });
    return docRef.id;
};

/**
 * Set a document (with specific ID)
 */
export const setDocument = async <T = DocumentData>(
    collectionName: string,
    documentId: string,
    data: T,
    merge = false
): Promise<void> => {
    const docRef = doc(db, collectionName, documentId);
    await setDoc(
        docRef,
        {
            ...data,
            updatedAt: Timestamp.now(),
        },
        { merge }
    );
};

/**
 * Update a document
 */
export const updateDocument = async <T = Partial<DocumentData>>(
    collectionName: string,
    documentId: string,
    data: T
): Promise<void> => {
    const docRef = doc(db, collectionName, documentId);
    await updateDoc(docRef, {
        ...data,
        updatedAt: Timestamp.now(),
    });
};

/**
 * Delete a document
 */
export const deleteDocument = async (
    collectionName: string,
    documentId: string
): Promise<void> => {
    const docRef = doc(db, collectionName, documentId);
    await deleteDoc(docRef);
};

/**
 * Listen to document changes
 */
export const subscribeToDocument = <T = DocumentData>(
    collectionName: string,
    documentId: string,
    callback: (data: T | null) => void
) => {
    const docRef = doc(db, collectionName, documentId);
    return onSnapshot(docRef, (docSnap) => {
        if (docSnap.exists()) {
            callback({ id: docSnap.id, ...docSnap.data() } as T);
        } else {
            callback(null);
        }
    });
};

/**
 * Listen to collection changes
 */
export const subscribeToCollection = <T = DocumentData>(
    collectionName: string,
    constraints: QueryConstraint[] = [],
    callback: (data: T[]) => void
) => {
    const collectionRef = collection(db, collectionName);
    const q = query(collectionRef, ...constraints);

    return onSnapshot(q, (querySnapshot) => {
        const data = querySnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data(),
        })) as T[];
        callback(data);
    });
};

/**
 * Helper to create where constraints
 */
export const createWhereConstraint = (
    field: string,
    operator: WhereFilterOp,
    value: any
): QueryConstraint => {
    return where(field, operator, value);
};

/**
 * Helper to create orderBy constraints
 */
export const createOrderByConstraint = (
    field: string,
    direction: OrderByDirection = 'asc'
): QueryConstraint => {
    return orderBy(field, direction);
};

/**
 * Helper to create limit constraint
 */
export const createLimitConstraint = (count: number): QueryConstraint => {
    return limit(count);
};

/**
 * Create a GeoPoint
 */
export const createGeoPoint = (latitude: number, longitude: number): GeoPoint => {
    return new GeoPoint(latitude, longitude);
};

/**
 * Create a Timestamp
 */
export const createTimestamp = (date?: Date): Timestamp => {
    return date ? Timestamp.fromDate(date) : Timestamp.now();
};

// Export Firestore types for convenience
export type {
    DocumentData,
    QueryConstraint,
    DocumentReference,
    CollectionReference,
    Timestamp,
    GeoPoint,
    WhereFilterOp,
    OrderByDirection,
};

export { where, orderBy, limit, startAfter };
